import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";

export default function WebsiteCoursesPage() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [saving, setSaving] = useState();
    const [error, setError] = useState();
    const [record, setRecord] = useState();

    useEffect(() => {
        requestAPI({
            requestPath: "website/courses-page",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRecord(data);
                else setError("Couldn't load Courses Page");
            },
        });
    }, [requestAPI]);

    const saveRecord = () => {
        requestAPI({
            requestPath: "website/courses-page",
            requestMethod: "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Update Courses Page !", life: 2000 }),
            onResponseReceieved: (updated, responseCode) => {
                if (updated && responseCode === 200) {
                    setRecord(updated);
                    showToast({ severity: "success", summary: "Updated", detail: "Courses Page Updated", life: 1000 });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: updated?.error || "Failed To Update Courses Page !", life: 2000 });
                }
            },
        });
    };

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Courses Page" />
            <TabHeader
                className="px-3 pt-3"
                title="Courses page text"
                highlights={["These words appear on the Courses page, above and below the course cards"]}
            />
            <Divider />
            {loading ? <Loading /> : null}
            {error ? <NoContent error={error} /> : null}
            {!loading && !error && record ? (
                <div className="flex-1 overflow-y-scroll p-3 flex flex-column">
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="eyebrow" className="w-full" value={record?.eyebrow ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, eyebrow: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="eyebrow">Small line above the heading</label>
                        </FloatLabel>
                        <FieldNote text="Example: Academic Programs." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="title">Page heading</label>
                        </FloatLabel>
                        <FieldNote text="The large heading at the top of the Courses page." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="description" className="w-full" rows={4} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                            <label htmlFor="description">Line under the heading</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="intro_title" className="w-full" value={record?.intro_title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, intro_title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="intro_title">Introduction heading</label>
                        </FloatLabel>
                        <FieldNote text="The block between the page heading and the course cards." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="intro_text" className="w-full" rows={4} autoResize value={record?.intro_text || ""} onChange={(event) => setRecord((prev) => ({ ...prev, intro_text: event.target.value }))} disabled={saving} />
                            <label htmlFor="intro_text">Introduction paragraph</label>
                        </FloatLabel>
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="show_search" checked={!!record?.show_search} onChange={(event) => setRecord((prev) => ({ ...prev, show_search: event.checked }))} disabled={saving} />
                        <label htmlFor="show_search" className={TEXT_SMALL}>Show the search box</label>
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="show_filters" checked={!!record?.show_filters} onChange={(event) => setRecord((prev) => ({ ...prev, show_filters: event.checked }))} disabled={saving} />
                        <label htmlFor="show_filters" className={TEXT_SMALL}>Show the filter chips</label>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="cta_title" className="w-full" value={record?.cta_title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, cta_title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="cta_title">Bottom heading</label>
                        </FloatLabel>
                        <FieldNote text="The dark block under the course cards." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="cta_description" className="w-full" rows={4} autoResize value={record?.cta_description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, cta_description: event.target.value }))} disabled={saving} />
                            <label htmlFor="cta_description">Line under the bottom heading</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="cta_primary_text" className="w-full" value={record?.cta_primary_text ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, cta_primary_text: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="cta_primary_text">Enquire button text</label>
                        </FloatLabel>
                        <FieldNote text="Leave blank to use Enquire Now." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="cta_secondary_url" className="w-full" value={record?.cta_secondary_url ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, cta_secondary_url: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="cta_secondary_url">App download link</label>
                        </FloatLabel>
                        <FieldNote text="Leave blank to hide the download button in that bottom block." />
                    </div>
                    <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={saveRecord} />
                </div>
            ) : null}
        </div>
    );
}
